export const CONSENT_STORAGE_KEY = "vf_cookie_consent";
export const CONSENT_SCHEMA_VERSION = 2;
// Not bumped for PostHog on purpose: the rollback target (live bdb956a)
// accepts only "2026-08-30" and "2026-08-28" and replaces any other record
// with a granted default, so a newer stamp would turn stored opt-outs back on
// after a rollback. Bump only once the rollback target accepts the new value.
export const CONSENT_POLICY_VERSION = "2026-08-30";
// Records saved under these earlier disclosure versions stay valid and are
// carried forward with the same choices (index.html keeps the same list).
export const CONSENT_CARRIED_FORWARD_POLICY_VERSIONS: readonly string[] = [
  "2026-08-28",
];
export const CONSENT_PREVIOUS_POLICY_VERSION =
  CONSENT_CARRIED_FORWARD_POLICY_VERSIONS[0];
export const CONSENT_MAX_AGE_DAYS = 180;
// Durable first-party copy of an explicit choice. Script-written storage can
// be capped by the browser (Safari ITP: 7 days), so the server re-issues the
// same value as an HTTP cookie through PRIVACY_CHOICE_ENDPOINT.
export const PRIVACY_COOKIE_NAME = "vf_privacy";
export const PRIVACY_COOKIE_MAX_AGE_SECONDS = 34560000; // 400 days
export const PRIVACY_COOKIE_REFRESH_DAYS = 30;
export const PRIVACY_CHOICE_ENDPOINT = "/api/privacy-choice";
export const GOOGLE_CONSENT_BOOTSTRAP_ERROR_EVENT =
  "vf:google-consent-bootstrap-error";
export const GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE =
  "google_consent_bootstrap_missing";

export type ConsentChoice = {
  analytics: boolean;
  marketing: boolean;
};

export type ConsentMethod =
  | "accept_all"
  | "reject_optional"
  | "customize"
  | "gpc"
  | "us_analytics_default"
  | "us_opt_out_default";

export type PrivacyCookie = ConsentChoice & {
  // UTC midnight of the issue date written in the cookie value.
  issuedAt: number;
};

export type ConsentRecord = ConsentChoice & {
  version: number;
  policyVersion: string;
  updatedAt: string;
  expiresAt: string;
  method: ConsentMethod;
  gpc: boolean;
};

export type ConsentUpdateResult = ConsentRecord & {
  reloadRequired: boolean;
};

export type GoogleConsentBootstrapError = {
  code: typeof GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE;
  message: string;
  missing: ("dataLayer" | "gtag" | "consent_default")[];
};

export type ConsentApi = {
  hasChoice: () => boolean;
  read: () => ConsentRecord | null;
  has: (category: keyof ConsentChoice) => boolean;
  isGpcEnabled: () => boolean;
  update: (
    choice: ConsentChoice,
    method?: ConsentMethod
  ) => ConsentUpdateResult;
};

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }

  interface Window {
    vfConsent?: ConsentApi;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __vfGoogleConsentDefaulted?: boolean;
    __vfGoogleConsentBootstrapError?: GoogleConsentBootstrapError;
    fbq?: ((...args: unknown[]) => void) & Record<string, unknown>;
    _fbq?: unknown;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";

export const isConsentRecordValid = (
  value: unknown,
  now = Date.now()
): value is ConsentRecord => {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ConsentRecord>;
  const policyVersionSupported =
    record.policyVersion === CONSENT_POLICY_VERSION ||
    CONSENT_CARRIED_FORWARD_POLICY_VERSIONS.includes(
      String(record.policyVersion)
    );
  if (
    record.version !== CONSENT_SCHEMA_VERSION ||
    !policyVersionSupported ||
    !isBoolean(record.analytics) ||
    !isBoolean(record.marketing) ||
    !isBoolean(record.gpc) ||
    typeof record.updatedAt !== "string" ||
    typeof record.expiresAt !== "string" ||
    typeof record.method !== "string"
  ) {
    return false;
  }
  const expiresAt = Date.parse(record.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > now;
};

export const applyPrivacySignals = (
  choice: ConsentChoice,
  gpc: boolean
): ConsentChoice => ({
  analytics: Boolean(choice.analytics),
  // GPC is a legally recognized opt-out signal in several US states. It must
  // override an older advertising preference on this browser/device.
  marketing: gpc ? false : Boolean(choice.marketing),
});

const readStoredRecord = (): ConsentRecord | null => {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isConsentRecordValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const persistRecord = (record: ConsentRecord): void => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Privacy choices still apply for the current page when storage is blocked.
  }
};

const gpcEnabled = (): boolean =>
  typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;

// v1.a{0|1}.m{0|1}.{YYYYMMDD}; the date is the UTC issue date. index.html
// parses the same shape before any tag can run.
const PRIVACY_COOKIE_VALUE =
  /^v1\.a([01])\.m([01])\.(\d{4})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/;

// Only a visitor's own choice is made durable. Automatic defaults never are.
const EXPLICIT_METHODS: readonly ConsentMethod[] = [
  "accept_all",
  "reject_optional",
  "customize",
  "gpc",
];
// A record that predates the cookie is copied into it only when it is
// unambiguously the visitor's choice; "gpc" records can also come from the
// automatic GPC migration of a default record.
const BACKFILL_METHODS: readonly ConsentMethod[] = [
  "accept_all",
  "reject_optional",
  "customize",
];

export const formatPrivacyCookieValue = (
  choice: ConsentChoice,
  now = Date.now()
): string =>
  `v1.a${choice.analytics ? 1 : 0}.m${choice.marketing ? 1 : 0}.${new Date(now)
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, "")}`;

export const parsePrivacyCookieValue = (
  value: unknown
): PrivacyCookie | null => {
  const match = PRIVACY_COOKIE_VALUE.exec(String(value ?? "").trim());
  if (!match) return null;
  return {
    analytics: match[1] === "1",
    marketing: match[2] === "1",
    issuedAt: Date.UTC(
      Number(match[3]),
      Number(match[4]) - 1,
      Number(match[5])
    ),
  };
};

export const readPrivacyCookie = (
  cookieHeader?: string
): PrivacyCookie | null => {
  let source = cookieHeader;
  if (source === undefined) {
    try {
      source = typeof document === "undefined" ? "" : document.cookie;
    } catch {
      source = "";
    }
  }
  for (const part of String(source || "").split(";")) {
    const separator = part.indexOf("=");
    if (separator < 0) continue;
    if (part.slice(0, separator).trim() !== PRIVACY_COOKIE_NAME) continue;
    const parsed = parsePrivacyCookieValue(part.slice(separator + 1));
    if (parsed) return parsed;
  }
  return null;
};

export const isPrivacyCookieRefreshDue = (
  cookie: PrivacyCookie,
  now = Date.now()
): boolean => now - cookie.issuedAt > PRIVACY_COOKIE_REFRESH_DAYS * DAY_MS;

const writePrivacyCookie = (choice: ConsentChoice): void => {
  try {
    const secure = window.location?.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${PRIVACY_COOKIE_NAME}=${formatPrivacyCookieValue(
      choice
    )}; Max-Age=${PRIVACY_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Cookies can be disabled; the local record still applies to this page.
  }
};

// The server answers with the same cookie set over HTTP, which browsers do not
// cap the way they cap script-written storage. Fire-and-forget by design.
const postPrivacyChoice = (choice: ConsentChoice): void => {
  try {
    if (typeof window.fetch !== "function") return;
    void window
      .fetch(PRIVACY_CHOICE_ENDPOINT, {
        method: "POST",
        keepalive: true,
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          analytics: Boolean(choice.analytics),
          marketing: Boolean(choice.marketing),
        }),
      })
      .catch(() => undefined);
  } catch {
    // Privacy choices must never interrupt the storefront.
  }
};

const rememberPrivacyChoice = (choice: ConsentChoice): void => {
  writePrivacyCookie(choice);
  postPrivacyChoice(choice);
};

const cookieMethod = (choice: ConsentChoice): ConsentMethod =>
  !choice.analytics && !choice.marketing ? "reject_optional" : "customize";

// Runs once per page load with the effective record.
const keepPrivacyCookieFresh = (record: ConsentRecord): void => {
  const cookie = readPrivacyCookie();
  if (cookie) {
    // A local denial is carried into the cookie; marketing that is off only
    // because of GPC is never made durable.
    const durable = {
      analytics: cookie.analytics && record.analytics,
      marketing: cookie.marketing && (record.marketing || record.gpc),
    };
    // Two overlapping choice requests can leave the server cookie on the
    // older, more permissive choice. Repair it now, not only at the refresh.
    const morePermissive =
      EXPLICIT_METHODS.includes(record.method) &&
      (cookie.analytics !== durable.analytics ||
        cookie.marketing !== durable.marketing);
    if (morePermissive) {
      rememberPrivacyChoice(durable);
    } else if (isPrivacyCookieRefreshDue(cookie)) {
      // Slide the 400-day window.
      postPrivacyChoice(durable);
    }
    return;
  }
  // An explicit choice saved before the durable cookie existed, or a cookie
  // that was lost. A "gpc" record may be the automatic GPC migration of a
  // default, so only its Analytics opt-out is certainly the visitor's own.
  const explicit =
    BACKFILL_METHODS.includes(record.method) ||
    (record.method === "gpc" && !record.analytics);
  if (!explicit) return;
  const durable = {
    analytics: record.analytics,
    marketing: record.marketing || record.gpc,
  };
  writePrivacyCookie(durable);
  // Ask the server only when this browser keeps cookies at all.
  if (readPrivacyCookie()) postPrivacyChoice(durable);
};

const metaContent = (name: string): string =>
  document
    .querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
    ?.content.trim() || "";

const googleConsentParams = (choice: ConsentChoice) => ({
  analytics_storage: choice.analytics ? "granted" : "denied",
  ad_storage: choice.marketing ? "granted" : "denied",
  ad_user_data: choice.marketing ? "granted" : "denied",
  ad_personalization: choice.marketing ? "granted" : "denied",
  functionality_storage: "granted",
  security_storage: "granted",
});

type GoogleConsentBootstrap = {
  dataLayer: unknown[];
  gtag: (...args: unknown[]) => void;
};

const reportMissingGoogleConsentBootstrap = (
  missing: GoogleConsentBootstrapError["missing"]
): void => {
  if (window.__vfGoogleConsentBootstrapError) return;

  const diagnostic: GoogleConsentBootstrapError = {
    code: GOOGLE_CONSENT_BOOTSTRAP_ERROR_CODE,
    message: `GTM was not loaded because the synchronous Google consent bootstrap is missing: ${missing.join(
      ", "
    )}.`,
    missing,
  };
  window.__vfGoogleConsentBootstrapError = diagnostic;

  // Analytics failures must remain non-fatal for the storefront, while still
  // being visible to monitoring and browser diagnostics.
  try {
    console.error(`[VueFront privacy] ${diagnostic.message}`);
  } catch {
    // Some hardened browsers replace or block the console object.
  }
  try {
    window.dispatchEvent(
      new CustomEvent<GoogleConsentBootstrapError>(
        GOOGLE_CONSENT_BOOTSTRAP_ERROR_EVENT,
        { detail: diagnostic }
      )
    );
  } catch {
    // A diagnostic listener must never be able to interrupt the storefront.
  }
};

const getGoogleConsentBootstrap = (): GoogleConsentBootstrap | null => {
  // Once the synchronous bootstrap has been missed, keep Google disabled for
  // this page. Loading it later would no longer preserve consent-before-tags.
  if (window.__vfGoogleConsentBootstrapError) return null;

  const missing: GoogleConsentBootstrapError["missing"] = [];
  if (!Array.isArray(window.dataLayer)) missing.push("dataLayer");
  if (typeof window.gtag !== "function") missing.push("gtag");
  if (window.__vfGoogleConsentDefaulted !== true) {
    missing.push("consent_default");
  }
  if (missing.length) {
    reportMissingGoogleConsentBootstrap(missing);
    return null;
  }

  return {
    dataLayer: window.dataLayer as unknown[],
    gtag: window.gtag as (...args: unknown[]) => void,
  };
};

const loadGoogleTagManager = (choice: ConsentChoice): void => {
  const containerId = metaContent("vf:gtm-container");
  if (!/^GTM-[A-Z0-9]+$/.test(containerId)) return;

  const bootstrap = getGoogleConsentBootstrap();
  if (!bootstrap) return;

  bootstrap.gtag("consent", "update", googleConsentParams(choice));
  if (document.querySelector("script[data-vf-gtm]")) return;

  bootstrap.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId
  )}`;
  script.setAttribute("data-vf-gtm", containerId);
  document.head.appendChild(script);
};

const loadMetaPixel = (): void => {
  const pixelId = metaContent("vf:meta-pixel");
  if (!/^\d+$/.test(pixelId)) return;

  if (window.fbq) {
    window.fbq("consent", "grant");
    return;
  }

  const queue: unknown[][] = [];
  const fbq = ((...args: unknown[]) => {
    // Meta replaces callMethod when its runtime arrives. Keep forwarding live
    // events after that point instead of accumulating them in a dead queue.
    if (typeof fbq?.callMethod === "function") fbq.callMethod(...args);
    else queue.push(args);
  }) as Window["fbq"];
  if (!fbq) return;
  fbq.queue = queue;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;
  fbq("init", pixelId);
  fbq("consent", "grant");
  fbq("track", "PageView");

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.setAttribute("data-vf-meta", pixelId);
  document.head.appendChild(script);
};

const expireCookie = (name: string): void => {
  const encoded = encodeURIComponent(name);
  const paths = ["/", window.location.pathname || "/"];
  const host = window.location.hostname;
  const domains = ["", host, host ? `.${host.replace(/^www\./, "")}` : ""];
  paths.forEach((path) => {
    domains.forEach((domain) => {
      document.cookie = `${encoded}=; Max-Age=0; Path=${path}; SameSite=Lax${
        domain ? `; Domain=${domain}` : ""
      }`;
    });
  });
};

const clearVendorStorage = (choice: ConsentChoice): void => {
  if (!choice.analytics) {
    try {
      window.localStorage.removeItem("vf_rum_seen");
    } catch {
      /* Optional storage. */
    }
  }
  // PostHog: ph_<token>_posthog (identity) and __ph_opt_in_out_<token>.
  const analyticsPrefixes = [
    "_ga",
    "_gid",
    "_gat",
    "_clck",
    "_clsk",
    "ph_",
    "__ph_",
  ];
  const marketingPrefixes = [
    "_gcl_",
    "_fbp",
    "_fbc",
    "__kla",
    "kla_",
    "_pin_",
    "_pinterest_",
    "_epik",
    "_derived_epik",
  ];
  const prefixes = [
    ...(choice.analytics ? [] : analyticsPrefixes),
    ...(choice.marketing ? [] : marketingPrefixes),
  ];

  document.cookie
    .split(";")
    .map((part) => decodeURIComponent(part.split("=", 1)[0].trim()))
    // The durable privacy choice itself is never removed by a cleanup.
    .filter((name) => name !== PRIVACY_COOKIE_NAME)
    .filter((name) => prefixes.some((prefix) => name.startsWith(prefix)))
    .forEach(expireCookie);

  if (!choice.analytics) {
    try {
      // PostHog keeps its identity and settings under ph_* in both storages
      // and its own opt-in/out flag under __ph_opt_in_out_*.
      [window.localStorage, window.sessionStorage].forEach((storage) => {
        Object.keys(storage)
          .filter((key) => /^(?:ph_|__ph)/.test(key))
          .forEach((key) => storage.removeItem(key));
      });
    } catch {
      // Storage may be unavailable in hardened privacy modes.
    }
  }

  if (!choice.marketing) {
    try {
      Object.keys(window.localStorage)
        .filter((key) => /klaviyo|^_?kl_/i.test(key))
        .forEach((key) => window.localStorage.removeItem(key));
      Object.keys(window.sessionStorage)
        .filter((key) => /klaviyo|^_?kl_/i.test(key))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      // Storage may be unavailable in hardened privacy modes.
    }
  }
};

const applyVendors = (choice: ConsentChoice): void => {
  // GTM must also load for a fully denied choice so Consent Mode can receive
  // and expose the denied state. Individual tags remain consent-gated in GTM.
  loadGoogleTagManager(choice);
  if (choice.marketing) {
    loadMetaPixel();
  } else {
    window.fbq?.("consent", "revoke");
  }
  clearVendorStorage(choice);
};

const dispatchUpdate = (record: ConsentRecord): void => {
  window.dispatchEvent(
    new CustomEvent<ConsentRecord>("vf:consent-updated", { detail: record })
  );
};

export const installConsentManager = (): ConsentApi | null => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  if (window.vfConsent) return window.vfConsent;

  const migrateStored = (
    stored: ConsentRecord,
    gpc: boolean
  ): ConsentRecord => {
    if (stored.method === "us_analytics_default") {
      // Migrate only the former automatic default. Explicit Reject/Customize
      // choices are preserved exactly as the visitor set them.
      const migrated = createRecord(
        { analytics: true, marketing: true },
        gpc ? "gpc" : "us_opt_out_default",
        gpc
      );
      persistRecord(migrated);
      return migrated;
    }
    if (gpc && stored.marketing) {
      const forced = createRecord(
        { analytics: stored.analytics, marketing: false },
        "gpc",
        true
      );
      persistRecord(forced);
      return forced;
    }
    if (stored.policyVersion !== CONSENT_POLICY_VERSION) {
      // Carry explicit choices forward when the disclosure text changes. A
      // policy-version migration must never turn a previous opt-out back on.
      const migrated = createRecord(
        { analytics: stored.analytics, marketing: stored.marketing },
        stored.method,
        gpc
      );
      persistRecord(migrated);
      return migrated;
    }
    return stored;
  };

  const read = (): ConsentRecord | null => {
    const stored = readStoredRecord();
    const cookie = readPrivacyCookie();
    const gpc = gpcEnabled();

    if (!stored) {
      if (!cookie) return null;
      // Browser storage was cleared or capped, but the durable choice
      // survived. Rebuild from it; never fall back to a granted default.
      const choice = {
        analytics: cookie.analytics,
        marketing: cookie.marketing,
      };
      const rebuilt = createRecord(choice, cookieMethod(choice), gpc);
      persistRecord(rebuilt);
      return rebuilt;
    }

    const record = migrateStored(stored, gpc);
    if (
      !cookie ||
      ((!record.analytics || cookie.analytics) &&
        (!record.marketing || cookie.marketing))
    ) {
      return record;
    }
    // A durable opt-out wins over a local record that grants the category.
    // Only update() with an explicit grant rewrites the cookie and lifts it;
    // a cookie grant never overrides a local denial.
    const choice = {
      analytics: record.analytics && cookie.analytics,
      marketing: record.marketing && cookie.marketing,
    };
    const forced = createRecord(choice, cookieMethod(choice), gpc);
    persistRecord(forced);
    return forced;
  };

  const api: ConsentApi = {
    hasChoice: () => Boolean(read()),
    read,
    has: (category) => Boolean(read()?.[category]),
    isGpcEnabled: gpcEnabled,
    update: (choice, method = "customize") => {
      const previous = read();
      const gpc = gpcEnabled();
      const record = createRecord(choice, gpc ? "gpc" : method, gpc);
      const reloadRequired = Boolean(
        (previous?.analytics && !record.analytics) ||
          (previous?.marketing && !record.marketing)
      );
      persistRecord(record);
      // Before vendors and listeners run, so every later read() already sees
      // the new durable value (a stale cookie denial would otherwise win).
      if (EXPLICIT_METHODS.includes(record.method))
        rememberPrivacyChoice(record);
      applyVendors(record);
      dispatchUpdate(record);
      return { ...record, reloadRequired };
    },
  };

  window.vfConsent = api;
  const initial = read();
  if (initial) {
    keepPrivacyCookieFresh(initial);
    applyVendors(initial);
  } else if (metaContent("vf:consent-experience") === "us-opt-out-default") {
    // The US storefront uses an opt-out experience for both optional
    // categories. GPC continues to override advertising on every visit.
    const defaultRecord = createRecord(
      { analytics: true, marketing: true },
      "us_opt_out_default",
      gpcEnabled()
    );
    persistRecord(defaultRecord);
    applyVendors(defaultRecord);
    dispatchUpdate(defaultRecord);
  } else {
    applyVendors({ analytics: false, marketing: false });
  }
  return api;
};

export const createRecord = (
  choice: ConsentChoice,
  method: ConsentMethod,
  gpc: boolean,
  now = Date.now()
): ConsentRecord => {
  const normalized = applyPrivacySignals(choice, gpc);
  return {
    version: CONSENT_SCHEMA_VERSION,
    policyVersion: CONSENT_POLICY_VERSION,
    ...normalized,
    updatedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + CONSENT_MAX_AGE_DAYS * DAY_MS).toISOString(),
    method,
    gpc,
  };
};
